<script setup lang="ts">
import { computed, watch, ref } from 'vue'
import { useUIStore } from '@/stores/uiStore'
import { useGlobalStore } from '@/stores/globalStore'
import { useBrandingStore } from '@/stores/brandingStore'
import { animate, durations, easings } from '@/lib/motion'

/**
 * Full-viewport splash overlay shown between route transitions.
 *
 * Lifecycle:
 *   1. router.beforeEach → uiStore.startNavigation() flips
 *      `navigating` true → splash mounts immediately.
 *   2. router.afterEach → uiStore.finishNavigation() flips false →
 *      splash fades OUT and unmounts.
 *
 * Brand mark mirrors `Header.vue`'s cascade (kept in sync by intent
 * — operator sees the same identity here as on the page chrome):
 *   1. Uploaded site logo  → operator-supplied image
 *   2. Company name        → typed text
 *   3. Default logo        → theme-flipped `/storage/brand/logo-
 *                            horizontal-{dark,light}.{webp,png}`
 *   4. App name            → Vite env / 'HonestWaves'
 *
 * History: a `DELAY_MS = 150` suppression existed earlier to avoid
 * flashing the overlay on truly-instant navs. Removed 2026-05-14
 * per user request — they want the splash on every navigation
 * (initial load, refresh, AND in-app routing) for a consistent
 * "loading" affordance. In-app nav typically resolves fast enough
 * that the splash + its fade is brief and not visually jarring.
 */
const ui = useUIStore()
const globalStore = useGlobalStore()
const brandingStore = useBrandingStore()

const appName = import.meta.env.VITE_APP_NAME ?? 'HonestWaves'

const uploadedLogo = computed<string | null>(() => {
  const slot = brandingStore.asset('logo')
  return slot && slot.is_default === false ? slot.url : null
})
const companyName = computed<string | null>(() => brandingStore.companyName)

const logoBase = computed<string>(() =>
  globalStore.currentTheme === 'dark'
    ? '/storage/brand/logo-horizontal-dark'
    : '/storage/brand/logo-horizontal-light',
)
const logoWebp = computed<string>(() => `${logoBase.value}.webp`)
const logoPng = computed<string>(() => `${logoBase.value}.png`)

const logoLoaded = ref<boolean>(true)
const onLogoError = (): void => { logoLoaded.value = false }

const visible = ref(false)
const splashEl = ref<HTMLElement | null>(null)
let shownAt = 0
let pendingHide: number | undefined

/**
 * Minimum on-screen time. Without this floor, instant navs (cached
 * chunks, no auth probe latency) would flash the splash for ~30ms —
 * a perceptible flicker that reads as visual noise. 500ms gives the
 * fade-in time to complete + a beat of brand presence before the
 * fade-out starts. The cap on the upper bound is the router lifecycle
 * (afterEach fires when nav settles); we only floor the bottom.
 */
const MIN_VISIBLE_MS = 500

const navigating = computed(() => ui.navigating)

watch(navigating, (active) => {
  if (active) {
    // Cancel any pending hide from a prior cycle — we're starting
    // a fresh navigation while the splash was still fading from the
    // previous one.
    if (pendingHide !== undefined) {
      window.clearTimeout(pendingHide)
      pendingHide = undefined
    }
    visible.value = true
    shownAt = Date.now()
  } else if (visible.value) {
    const elapsed = Date.now() - shownAt
    const remaining = Math.max(0, MIN_VISIBLE_MS - elapsed)
    if (remaining === 0) {
      fadeOutThenHide()
    } else {
      pendingHide = window.setTimeout(() => {
        pendingHide = undefined
        fadeOutThenHide()
      }, remaining)
    }
  }
})

function onEnter(el: Element, done: () => void) {
  splashEl.value = el as HTMLElement
  animate(el, {
    opacity: [0, 1],
    duration: durations.normal,
    ease: easings.decel,
    onComplete: done,
  })
}

function onLeave(el: Element, done: () => void) {
  animate(el, {
    opacity: [1, 0],
    duration: durations.quick,
    ease: easings.decel,
    onComplete: done,
  })
}

function fadeOutThenHide() {
  visible.value = false
}
</script>

<template>
  <Transition :css="false" @enter="onEnter" @leave="onLeave">
    <div v-if="visible" class="nav-splash" role="status" aria-live="polite" aria-busy="true">
      <div class="nav-splash-content">
        <!-- Brand mark cascade — mirrors Header.vue. Operator sees the
             same identity on the splash as on the page chrome. -->
        <div class="nav-splash-brand">
          <img
            v-if="uploadedLogo"
            :src="uploadedLogo"
            :alt="companyName ?? appName"
            class="nav-splash-img"
          />
          <h1
            v-else-if="companyName"
            class="nav-splash-logo"
          >{{ companyName }}</h1>
          <picture v-else-if="logoLoaded">
            <source :srcset="logoWebp" type="image/webp" />
            <img
              :src="logoPng"
              :alt="appName"
              class="nav-splash-img"
              @error="onLogoError"
            />
          </picture>
          <h1 v-else class="nav-splash-logo">{{ appName }}</h1>
        </div>
        <ProgressSpinner style="width: 48px; height: 48px" strokeWidth="5" />
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.nav-splash {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--theme-canvas);
  /* Slight translucency so a flash of the underlying view shows
     through — feels less like a hard cut. */
  background-color: color-mix(in srgb, var(--theme-canvas) 92%, transparent);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  opacity: 0; /* anime.js sets to 1 on enter */
  will-change: opacity;
}

.nav-splash-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--theme-space-6);
}

.nav-splash-brand {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 2.5rem;
}
.nav-splash-img {
  height: 2.5rem;
  width: auto;
  display: block;
}
.nav-splash-logo {
  margin: 0;
  font-family: var(--theme-font-sans);
  font-size: var(--theme-text-xl);
  font-weight: var(--theme-weight-semibold);
  letter-spacing: var(--theme-tracking-tight);
  color: var(--theme-text-primary);
}
</style>
